/**
 * @file apps/web/src/pages/TrackPage.tsx
 * @description UAT tracking dashboard for a project.
 * Shows overall UAT progress, session statuses with completion bars, and defect triage.
 * Route: /projects/:projectId/track
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  AlertCircle, Bug, CheckCircle, Clock, Play, Plus,
  ChevronRight, BarChart2, Target, Zap,
} from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { uatApi } from '../lib/api.js';
import { DefectList, type Defect } from '../components/DefectCard.js';
import { Badge } from '../components/Badge.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UatSession {
  id:            string;
  name:          string;
  status:        string;
  created_at:    string;
  scheduled_at?: string | null;
  started_at?:   string | null;
  completed_at?: string | null;
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

function ProgressBar({ value, className = '' }: { value: number; className?: string }) {
  return (
    <div className={['h-1.5 rounded-full bg-[rgba(8,17,31,0.06)] overflow-hidden', className].join(' ')}>
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.min(100, Math.max(0, value))}%`, backgroundColor: 'var(--blue)' }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Session row
// ---------------------------------------------------------------------------

function SessionRow({
  session,
  onAdvance,
}: {
  session:   UatSession;
  onAdvance: (id: string, status: string) => void;
}) {
  const nextStatus: Record<string, string | undefined> = {
    scheduled:   'in_progress',
    in_progress: 'completed',
  };

  const progress = {
    scheduled:   0,
    in_progress: 50,
    completed:   100,
  }[session.status] ?? 0;

  const icon = {
    scheduled:   <Clock size={13} className="text-warning" />,
    in_progress: <Play size={13} className="text-blue" />,
    completed:   <CheckCircle size={13} className="text-success" />,
  }[session.status] ?? <Clock size={13} className="text-muted" />;

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {icon}
          <div className="flex-1 min-w-0">
            <Link
              to={`/sessions/${session.id}`}
              className="text-sm font-medium text-ink hover:text-blue transition-colors truncate block"
            >
              {session.name}
            </Link>
            <p className="text-[11px] font-mono text-muted mt-0.5">
              {new Date(session.created_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric',
              })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={session.status as never} size="xs" />
          {nextStatus[session.status] && (
            <button
              onClick={() => onAdvance(session.id, nextStatus[session.status]!)}
              className="btn-secondary text-xs py-0.5 px-2"
            >
              Mark {nextStatus[session.status]?.replace('_', ' ')}
            </button>
          )}
          <Link
            to={`/sessions/${session.id}`}
            className="btn-icon text-muted hover:text-blue"
          >
            <ChevronRight size={14} />
          </Link>
        </div>
      </div>

      <ProgressBar value={progress} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  sub,
  icon,
  intent = 'neutral',
}: {
  label:   string;
  value:   number | string;
  sub?:    string;
  icon:    React.ReactNode;
  intent?: 'neutral' | 'success' | 'warning' | 'danger';
}) {
  const intentClass = {
    neutral: 'text-blue bg-sky',
    success: 'text-success bg-success/8',
    warning: 'text-warning bg-warning/8',
    danger:  'text-danger bg-danger/8',
  }[intent];

  return (
    <div className="card p-4 flex items-start gap-3">
      <div className={['w-8 h-8 rounded-lg flex items-center justify-center shrink-0', intentClass].join(' ')}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-display font-bold text-ink leading-none">{value}</p>
        <p className="text-xs font-medium text-muted mt-0.5">{label}</p>
        {sub && <p className="text-[11px] font-mono text-muted/60 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TrackPage
// ---------------------------------------------------------------------------

export function TrackPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user, devHeaders } = useAuth();

  const [sessions, setSessions]         = useState<UatSession[]>([]);
  const [defects, setDefects]           = useState<Defect[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [showLogDefect, setShowLogDefect]   = useState(false);
  const [newDefectTitle, setNewDefectTitle] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [sess, defs] = await Promise.all([
        uatApi.listSessions(projectId, devHeaders) as Promise<UatSession[]>,
        uatApi.listDefects(projectId, devHeaders) as Promise<Defect[]>,
      ]);
      setSessions(sess);
      setDefects(defs);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load UAT data');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleAdvanceSession = async (id: string, status: string) => {
    setSessions((prev) => prev.map((s) => s.id === id ? { ...s, status } : s));
    try {
      await uatApi.updateSession(id, { status }, devHeaders);
    } catch {
      await load();
    }
  };

  const handleDefectStatusChange = async (id: string, status: string) => {
    setDefects((prev) => prev.map((d) => d.id === id ? { ...d, status } : d));
    try {
      await uatApi.updateDefect(id, { status }, devHeaders);
    } catch {
      await load();
    }
  };

  const handleNewSession = async () => {
    if (!projectId || !newSessionName.trim()) return;
    try {
      const created = await uatApi.createSession({
        project_id:  projectId,
        name:        newSessionName.trim(),
        status:      'scheduled',
        created_by:  user.userId,
      }, devHeaders) as UatSession;
      setSessions((prev) => [...prev, created]);
      setShowNewSession(false);
      setNewSessionName('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create session');
    }
  };

  const handleLogDefect = async () => {
    if (!projectId || !newDefectTitle.trim()) return;
    try {
      const created = await uatApi.createDefect({
        project_id:  projectId,
        title:       newDefectTitle.trim(),
        status:      'open',
        severity:    'medium',
        reported_by: user.userId,
      }, devHeaders) as Defect;
      setDefects((prev) => [created, ...prev]);
      setShowLogDefect(false);
      setNewDefectTitle('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to log defect');
    }
  };

  // Derived stats
  const completed   = sessions.filter((s) => s.status === 'completed').length;
  const inProgress  = sessions.filter((s) => s.status === 'in_progress').length;
  const scheduled   = sessions.filter((s) => s.status === 'scheduled').length;
  const openDefects = defects.filter((d) => d.status !== 'resolved' && d.status !== 'closed');
  const closedDefects = defects.filter((d) => d.status === 'resolved' || d.status === 'closed');

  const overallProgress = sessions.length > 0
    ? Math.round((completed / sessions.length) * 100)
    : 0;

  return (
    <div className="p-8 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">UAT Tracking</h1>
          {!loading && (
            <p className="text-sm text-muted mt-1">
              {overallProgress}% complete · {sessions.length} session{sessions.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowLogDefect((v) => !v); setShowNewSession(false); }} className="btn-ghost text-sm">
            <Bug size={14} /> Log Defect
          </button>
          <button onClick={() => { setShowNewSession((v) => !v); setShowLogDefect(false); }} className="btn-primary text-sm">
            <Plus size={14} /> New Session
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {showNewSession && (
        <div className="card p-4 space-y-3 mb-4">
          <p className="text-sm font-medium text-ink">New UAT Session</p>
          <input
            type="text"
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            placeholder='e.g. "Sprint 4 UAT"'
            className="input"
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') void handleNewSession(); if (e.key === 'Escape') { setShowNewSession(false); setNewSessionName(''); } }}
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowNewSession(false); setNewSessionName(''); }} className="btn-ghost text-sm">Cancel</button>
            <button onClick={() => void handleNewSession()} disabled={!newSessionName.trim()} className="btn-primary text-sm">Create Session</button>
          </div>
        </div>
      )}

      {showLogDefect && (
        <div className="card p-4 space-y-3 mb-4">
          <p className="text-sm font-medium text-ink">Log Defect</p>
          <input
            type="text"
            value={newDefectTitle}
            onChange={(e) => setNewDefectTitle(e.target.value)}
            placeholder="Describe the defect…"
            className="input"
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') void handleLogDefect(); if (e.key === 'Escape') { setShowLogDefect(false); setNewDefectTitle(''); } }}
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowLogDefect(false); setNewDefectTitle(''); }} className="btn-ghost text-sm">Cancel</button>
            <button onClick={() => void handleLogDefect()} disabled={!newDefectTitle.trim()} className="btn-primary text-sm">Log Defect</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-4 animate-pulse">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1,2,3,4].map((i) => <div key={i} className="card h-20 bg-gray/60" />)}
          </div>
          <div className="card h-48 bg-gray/60" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              label="Sessions Completed"
              value={completed}
              sub={`of ${sessions.length}`}
              icon={<CheckCircle size={16} />}
              intent="success"
            />
            <StatCard
              label="In Progress"
              value={inProgress}
              icon={<Play size={16} />}
              intent={inProgress > 0 ? 'warning' : 'neutral'}
            />
            <StatCard
              label="Open Defects"
              value={openDefects.length}
              sub={`${closedDefects.length} resolved`}
              icon={<Bug size={16} />}
              intent={openDefects.length > 0 ? 'danger' : 'success'}
            />
            <StatCard
              label="Overall Progress"
              value={`${overallProgress}%`}
              icon={<BarChart2 size={16} />}
            />
          </div>

          {/* Overall progress bar */}
          {sessions.length > 0 && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-ink">UAT Completion</p>
                <p className="text-sm font-mono text-blue">{overallProgress}%</p>
              </div>
              <ProgressBar value={overallProgress} className="h-2" />
              <div className="flex items-center gap-4 mt-2 text-[11px] font-mono text-muted">
                <span>{completed} completed</span>
                <span>{inProgress} in progress</span>
                <span>{scheduled} scheduled</span>
              </div>
            </div>
          )}

          {/* Sessions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-sm font-semibold text-ink">Sessions</h2>
              <button
                onClick={() => { setShowNewSession(true); setShowLogDefect(false); }}
                className="flex items-center gap-1 text-xs text-muted hover:text-blue transition-colors"
              >
                <Plus size={12} /> Add session
              </button>
            </div>

            {sessions.length === 0 ? (
              <div className="empty-state py-10">
                <Target size={24} className="text-muted/40 mb-2 mx-auto" />
                <p className="text-sm text-muted">No UAT sessions yet.</p>
                <button onClick={() => { setShowNewSession(true); setShowLogDefect(false); }} className="btn-secondary mt-4">
                  <Plus size={14} /> Create Session
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    onAdvance={handleAdvanceSession}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Defects */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-sm font-semibold text-ink">
                Defects
                {openDefects.length > 0 && (
                  <span className="ml-2 text-xs font-mono px-1.5 py-0.5 rounded-full bg-danger/10 text-danger">
                    {openDefects.length} open
                  </span>
                )}
              </h2>
              <button
                onClick={() => { setShowLogDefect(true); setShowNewSession(false); }}
                className="flex items-center gap-1 text-xs text-muted hover:text-blue transition-colors"
              >
                <Plus size={12} /> Log defect
              </button>
            </div>

            {defects.length === 0 ? (
              <div className="empty-state py-10">
                <Zap size={24} className="text-muted/40 mb-2 mx-auto" />
                <p className="text-sm text-muted">No defects logged.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {openDefects.length > 0 && (
                  <div>
                    <p className="label mb-2">Open ({openDefects.length})</p>
                    <DefectList
                      defects={openDefects}
                      loading={false}
                      onStatusChange={handleDefectStatusChange}
                    />
                  </div>
                )}
                {closedDefects.length > 0 && (
                  <div>
                    <p className="label mb-2">Resolved / Closed ({closedDefects.length})</p>
                    <DefectList
                      defects={closedDefects}
                      loading={false}
                      onStatusChange={handleDefectStatusChange}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
